/**
 * Phase 267 — Super Admin CRM Directory + per-row Outreach Console.
 *
 * Distinct from /super-admin/walkthrough-blast (bulk select-then-blast)
 * — this surface targets per-recipient quick-action outreach across
 * three audiences (Academies, Students, Employees).
 *
 * Wires to:
 *   GET  /api/super-admin/crm-directory
 *   GET  /api/super-admin/crm-directory/meta-templates
 *   GET  /api/super-admin/crm-directory/cooldown
 *   POST /api/super-admin/crm-directory/outreach
 *   GET  /api/super-admin/crm-directory/logs
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Search, Users, GraduationCap, Briefcase, Mail, MessageCircle,
  Send, RefreshCw, AlertCircle, CheckCircle2, Clock, Loader2, History,
} from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const TAB_META = {
  academies: { icon: Users, label: 'Academies', singular: 'academy' },
  students: { icon: GraduationCap, label: 'Students', singular: 'student' },
  employees: { icon: Briefcase, label: 'Employees & Coaches', singular: 'employee' },
};

const COOLDOWN_HOURS = 24;

const formatTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  } catch { return iso; }
};

export default function SuperAdminCrmDirectory() {
  const [activeTab, setActiveTab] = useState('academies');
  const [directory, setDirectory] = useState({ academies: [], students: [], employees: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Outreach dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTarget, setDialogTarget] = useState(null);
  const [dialogTargetType, setDialogTargetType] = useState('');
  const [channel, setChannel] = useState('whatsapp');
  const [templates, setTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState('');
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedLang, setSelectedLang] = useState('en_US');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [cooldownInfo, setCooldownInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const [resultPanel, setResultPanel] = useState(null);

  // Logs drawer
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchDirectory = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : '';
      const res = await api.get(`/super-admin/crm-directory${params}`);
      setDirectory({
        academies: res.data.academies || [],
        students: res.data.students || [],
        employees: res.data.employees || [],
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load directory');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDirectory(''); }, [fetchDirectory]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchDirectory(search), 350);
    return () => clearTimeout(t);
  }, [search, fetchDirectory]);

  const rows = directory[activeTab] || [];

  const fetchMetaTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      const res = await api.get('/super-admin/crm-directory/meta-templates');
      setTemplates(res.data.templates || []);
      if (res.data.error) setTemplatesError(res.data.error);
    } catch (err) {
      setTemplatesError(err?.response?.data?.detail || 'Failed to load templates');
      setTemplates([]);
    } finally { setTemplatesLoading(false); }
  }, []);

  const checkCooldown = useCallback(async (recipient, recipientType, template, ch) => {
    if (!recipient?.id || !template) { setCooldownInfo(null); return; }
    try {
      const res = await api.get('/super-admin/crm-directory/cooldown', {
        params: {
          recipient_id: recipient.id, recipient_type: recipientType,
          template_name: template, channel: ch,
        },
      });
      setCooldownInfo(res.data);
    } catch { setCooldownInfo(null); }
  }, []);

  const openOutreach = (row, recipientType) => {
    setDialogTarget(row);
    setDialogTargetType(recipientType);
    setChannel('whatsapp');
    setSelectedTemplate('');
    setSelectedLang('en_US');
    setEmailSubject('');
    setEmailBody('');
    setCooldownInfo(null);
    setResultPanel(null);
    setDialogOpen(true);
    fetchMetaTemplates();
  };

  // Re-check cooldown when template / channel changes
  useEffect(() => {
    if (!dialogOpen || !dialogTarget) return;
    const tpl = channel === 'whatsapp' ? selectedTemplate : 'free_form_email';
    if (tpl) checkCooldown(dialogTarget, dialogTargetType, tpl, channel);
    else setCooldownInfo(null);
  }, [dialogOpen, dialogTarget, dialogTargetType, channel, selectedTemplate, checkCooldown]);

  const handleSend = async () => {
    if (!dialogTarget) return;
    if (channel === 'whatsapp' && !selectedTemplate) {
      toast.error('Select a Meta-approved template'); return;
    }
    if (channel === 'email' && (!emailSubject.trim() || !emailBody.trim())) {
      toast.error('Subject and body are required for Email'); return;
    }
    if (channel === 'whatsapp' && !dialogTarget.phone) {
      toast.error('Recipient has no phone number on file'); return;
    }
    if (channel === 'email' && !dialogTarget.email) {
      toast.error('Recipient has no email on file'); return;
    }

    setSending(true);
    setResultPanel(null);
    try {
      const payload = {
        recipient_id: dialogTarget.id,
        recipient_type: dialogTargetType,
        channel,
      };
      if (channel === 'whatsapp') {
        payload.template_name = selectedTemplate;
        payload.language_code = selectedLang || 'en_US';
      } else {
        payload.subject = emailSubject.trim();
        payload.body = emailBody.trim();
      }
      const res = await api.post('/super-admin/crm-directory/outreach', payload);
      setResultPanel({ http_success: true, status: res.data.status, payload: res.data });
      if (res.data.status === 'sent') {
        toast.success(`Sent via ${channel.toUpperCase()} to ${dialogTarget.name}`);
      } else {
        toast.error(`${channel.toUpperCase()} dispatch returned status=${res.data.status}`);
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Send failed';
      setResultPanel({ http_success: false, status, detail });
      if (status === 429) toast.error('Cooldown active — already sent within last 24h');
      else if (status === 412) toast.error('Save Resend credentials first');
      else toast.error(detail);
    } finally { setSending(false); }
  };

  // Phase 272 — non-opener filter for the logs drawer
  const [logsFilter, setLogsFilter] = useState('all');

  const loadLogs = useCallback(async (filter = 'all') => {
    setLogsLoading(true);
    try {
      const qs = filter && filter !== 'all' ? `&filter=${encodeURIComponent(filter)}` : '';
      const res = await api.get(`/super-admin/crm-directory/logs?limit=100${qs}`);
      setLogs(res.data.entries || []);
    } catch (err) {
      toast.error('Failed to load outreach logs');
    } finally { setLogsLoading(false); }
  }, []);

  useEffect(() => { if (logsOpen) loadLogs(logsFilter); }, [logsOpen, logsFilter, loadLogs]);

  const renderRow = (row, recipientType) => (
    <tr key={row.id} className="border-b border-border hover:bg-muted/40 transition-colors" data-testid={`crm-row-${recipientType}-${row.id}`}>
      <td className="py-3 px-2 text-sm font-medium">{row.name || '—'}</td>
      <td className="py-3 px-2 text-xs text-muted-foreground font-mono">{row.email || '—'}</td>
      <td className="py-3 px-2 text-xs text-muted-foreground font-mono">{row.phone || '—'}</td>
      <td className="py-3 px-2 text-xs text-muted-foreground">{row.academy_name || '—'}</td>
      <td className="py-3 px-2 text-right">
        <Button
          size="sm"
          variant="outline"
          onClick={() => openOutreach(row, recipientType)}
          data-testid={`crm-outreach-btn-${recipientType}-${row.id}`}
          className="gap-1.5"
        >
          <Send className="h-3.5 w-3.5" /> Outreach
        </Button>
      </td>
    </tr>
  );

  const tabCounts = useMemo(() => ({
    academies: directory.academies.length,
    students: directory.students.length,
    employees: directory.employees.length,
  }), [directory]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4" data-testid="crm-directory-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Super Admin CRM Directory</h1>
          <p className="text-sm text-muted-foreground mt-1">Per-row outreach across all Academies, Students, and Employees. WhatsApp via Meta Cloud · Email via Resend · 24h cooldown per template guards against accidental double-sends.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchDirectory(search)} disabled={loading} data-testid="crm-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setLogsOpen(true)} data-testid="crm-logs-btn">
            <History className="h-3.5 w-3.5 mr-1.5" /> Audit Log
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="relative max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, phone, or academy…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="crm-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3" data-testid="crm-tabs">
          {Object.entries(TAB_META).map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <TabsTrigger key={key} value={key} data-testid={`crm-tab-${key}`}>
                <Icon className="h-4 w-4 mr-2" /> {meta.label}
                <Badge variant="secondary" className="ml-2">{tabCounts[key]}</Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.keys(TAB_META).map((key) => (
          <TabsContent key={key} value={key}>
            <Card>
              <CardContent className="pt-4 overflow-x-auto">
                {loading ? (
                  <div className="py-12 text-center text-sm text-muted-foreground" data-testid={`crm-empty-${key}`}>
                    <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading directory…
                  </div>
                ) : rows.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground" data-testid={`crm-empty-${key}`}>
                    No records match. Try clearing the search.
                  </div>
                ) : (
                  <table className="w-full text-left" data-testid={`crm-table-${key}`}>
                    <thead>
                      <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                        <th className="py-2 px-2">Name</th>
                        <th className="py-2 px-2">Email</th>
                        <th className="py-2 px-2">Phone</th>
                        <th className="py-2 px-2">Academy</th>
                        <th className="py-2 px-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>{rows.map((row) => renderRow(row, TAB_META[key].singular))}</tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Outreach dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="crm-outreach-dialog">
          <DialogHeader>
            <DialogTitle>Send Outreach to {dialogTarget?.name || '—'}</DialogTitle>
            <DialogDescription>
              {dialogTargetType === 'academy' ? 'Academy owner' : dialogTargetType === 'student' ? 'Student / parent' : 'Employee / coach'} · {dialogTarget?.academy_name || 'No academy'}
              {dialogTarget?.email && <> · <span className="font-mono">{dialogTarget.email}</span></>}
              {dialogTarget?.phone && <> · <span className="font-mono">{dialogTarget.phone}</span></>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Channel selector */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={channel === 'whatsapp' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChannel('whatsapp')}
                disabled={!dialogTarget?.phone}
                data-testid="crm-channel-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
              </Button>
              <Button
                variant={channel === 'email' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChannel('email')}
                disabled={!dialogTarget?.email}
                data-testid="crm-channel-email-btn"
              >
                <Mail className="h-4 w-4 mr-2" /> Email
              </Button>
            </div>

            {/* WhatsApp config */}
            {channel === 'whatsapp' && (
              <div className="space-y-3" data-testid="crm-whatsapp-config">
                <div className="space-y-1.5">
                  <Label className="text-xs">Meta-approved template {templatesLoading && <Loader2 className="h-3 w-3 inline animate-spin ml-1" />}</Label>
                  {templatesError ? (
                    <div className="text-[11px] rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-2 text-amber-800 dark:text-amber-200" data-testid="crm-templates-error">
                      <AlertCircle className="h-3 w-3 inline mr-1" /> {templatesError}
                    </div>
                  ) : templates.length === 0 && !templatesLoading ? (
                    <div className="text-[11px] text-muted-foreground italic">No approved templates on your WABA yet.</div>
                  ) : (
                    <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                      <SelectTrigger data-testid="crm-template-select"><SelectValue placeholder="Choose a template…" /></SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={`${t.name}-${t.language}`} value={t.name} data-testid={`crm-template-${t.name}`}>
                            {t.name} · <span className="text-muted-foreground">{t.language} · {t.category}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Language code</Label>
                  <Input
                    value={selectedLang}
                    onChange={(e) => setSelectedLang(e.target.value)}
                    placeholder="en_US"
                    data-testid="crm-lang-input"
                  />
                </div>
              </div>
            )}

            {/* Email config */}
            {channel === 'email' && (
              <div className="space-y-3" data-testid="crm-email-config">
                <div className="space-y-1.5">
                  <Label className="text-xs">Subject</Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Master your academy operations with Out-Play"
                    data-testid="crm-email-subject-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Body (plain text — newlines preserved)</Label>
                  <Textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={6}
                    placeholder={`Hi ${dialogTarget?.name?.split(' ')[0] || ''},\n\nHere's a quick walkthrough video to get the most from your Out-Play dashboard:\nhttps://out-play.in/walkthrough\n\nLet's grow your club!`}
                    data-testid="crm-email-body-input"
                  />
                </div>
                <p className="text-[10px] text-muted-foreground">Sent from <span className="font-mono">contact@out-play.in</span> via Resend.</p>
              </div>
            )}

            {/* Cooldown warning */}
            {cooldownInfo?.on_cooldown && (
              <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-900 p-2.5 text-[11px] text-rose-800 dark:text-rose-200" data-testid="crm-cooldown-warning">
                <Clock className="h-3 w-3 inline mr-1" /> <strong>Cooldown active</strong> · last successful send: {formatTs(cooldownInfo.last_sent_at)} · retry after: {formatTs(cooldownInfo.expires_at)}
              </div>
            )}

            {/* Result panel */}
            {resultPanel && (
              <div
                className={`rounded-md border p-2.5 text-[11px] font-mono ${
                  resultPanel.http_success && resultPanel.status === 'sent'
                    ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200'
                    : 'border-rose-300 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30 text-rose-800 dark:text-rose-200'
                }`}
                data-testid="crm-outreach-result"
              >
                {resultPanel.http_success && resultPanel.status === 'sent' ? (
                  <><CheckCircle2 className="h-3 w-3 inline mr-1" /> <strong>Sent ✓</strong> · log_id: {resultPanel.payload?.log_id}</>
                ) : resultPanel.http_success ? (
                  <><AlertCircle className="h-3 w-3 inline mr-1" /> <strong>Status: {resultPanel.status}</strong> · {JSON.stringify(resultPanel.payload?.result || {}).slice(0, 200)}</>
                ) : (
                  <><AlertCircle className="h-3 w-3 inline mr-1" /> <strong>HTTP {resultPanel.status}</strong> · {resultPanel.detail}</>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)} data-testid="crm-cancel-btn">Cancel</Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={sending || cooldownInfo?.on_cooldown}
              data-testid="crm-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Confirm & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit log dialog */}
      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="max-w-3xl" data-testid="crm-logs-dialog">
          <DialogHeader>
            <DialogTitle>Outreach Audit Log (last 100)</DialogTitle>
            <DialogDescription>Reverse-chronological feed of every per-row dispatch.</DialogDescription>
          </DialogHeader>
          {/* Phase 272 — non-opener retargeting filter */}
          <div className="flex items-center justify-between gap-3 -mt-2 mb-1">
            <div className="flex items-center gap-2">
              <Label className="text-[11px] text-muted-foreground">Filter</Label>
              <Select value={logsFilter} onValueChange={setLogsFilter}>
                <SelectTrigger className="h-7 text-xs w-[200px]" data-testid="crm-logs-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="crm-logs-filter-all">All entries</SelectItem>
                  <SelectItem value="non_openers" data-testid="crm-logs-filter-non-openers">Target Non-Openers (email)</SelectItem>
                </SelectContent>
              </Select>
              {logsFilter === 'non_openers' && (
                <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-300">
                  Showing email sends &gt; 1h ago with no open pixel hit
                </Badge>
              )}
            </div>
            <Badge variant="outline" className="text-[10px]" data-testid="crm-logs-count-pill">{logs.length} rows</Badge>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {logsLoading ? (
              <div className="py-12 text-center text-sm text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading…</div>
            ) : logs.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">No outreach logged yet.</div>
            ) : (
              <table className="w-full text-left text-xs" data-testid="crm-logs-table">
                <thead>
                  <tr className="border-b border-border text-[10px] uppercase text-muted-foreground">
                    <th className="py-2 px-2">When</th>
                    <th className="py-2 px-2">Channel</th>
                    <th className="py-2 px-2">Recipient</th>
                    <th className="py-2 px-2">Template</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b border-border/50" data-testid={`crm-log-row-${l.id}`}>
                      <td className="py-2 px-2 font-mono">{formatTs(l.sent_at)}</td>
                      <td className="py-2 px-2">{l.channel}</td>
                      <td className="py-2 px-2">{l.recipient_name} <span className="text-muted-foreground">({l.recipient_type})</span></td>
                      <td className="py-2 px-2 font-mono">{l.template_name}</td>
                      <td className="py-2 px-2">
                        <Badge variant={l.status === 'sent' ? 'default' : 'destructive'}>{l.status}</Badge>
                      </td>
                      <td className="py-2 px-2" data-testid={`crm-log-engagement-${l.id}`}>
                        {l.channel === 'email' ? (
                          l.opens_count > 0 ? (
                            <span className="text-emerald-600 font-bold">{l.opens_count}× · first {formatTs(l.opened_at)}</span>
                          ) : l.status === 'sent' ? (
                            <span className="text-muted-foreground italic">Not opened yet</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : (
                          <span className="text-muted-foreground">n/a</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
